import React, { useEffect, useState } from "react";
 // Adjust the path according to your file structure
import { getAuth } from "firebase/auth";
import { addDoc, collection, deleteDoc, doc, getDocs, getFirestore, updateDoc } from "firebase/firestore"; // Firestore functions
import { motion } from "framer-motion";
import { Filter, Search } from "lucide-react";
import { ObjectEditor } from "../components/manipulation/ObjectEditor";
import { ObjectFilters } from "../components/manipulation/ObjectFilters";
import { ObjectsList } from "../components/manipulation/ObjectList";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "../components/ui/resizable";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { app } from "../firebase";

const objectTypes = [
    "certifiedStudents",
    "courses",
    "modules",
    "users",
    "courseAttandance",
    "courseBatches",
    "courseTodayClasses",
    "courseSubjects",
    "students",
    "employees",
    "allSubjects",
    "certifiedStudents",
    "roles"
  ];

export default function Manipulation() {
  const [selectedType, setSelectedType] = useState("courses");
  const [selectedObject, setSelectedObject] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [data, setData] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState("all");

    const db = getFirestore(app);
    const auth = getAuth(app);

  const handleTypeSelect = (type: string) => {
    setSelectedType(type);
    setSelectedObject(null);
    setSearchTerm("");
    setFilters({});
    fetchData(type); // Fetch data when type is selected
  };

  useEffect(() => {
    fetchData(selectedType); // Fetch data on initial render
  }, [selectedType]);

  const fetchData = async (type: string) => {
    const querySnapshot = await getDocs(collection(db, type)); // Fetch data from Firestore
    const fetchedData = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    setData(fetchedData);
  };

  const handleObjectSelect = (object: any) => {
    setSelectedObject(object);
  };

  const handleObjectUpdate = async (updatedObject: any) => {
    const docRef = doc(db, selectedType, updatedObject.id); // Reference to the document
    await updateDoc(docRef, updatedObject); // Update document in Firestore
    setData(data.map((item) => (item.id === updatedObject.id ? updatedObject : item)));
    setSelectedObject(updatedObject);
  };

  const handleObjectDelete = async (id: string) => {
    const docRef = doc(db, selectedType, id); // Reference to the document
    await deleteDoc(docRef); // Delete document in Firestore
    setData(data.filter((item) => item.id !== id));
    if (selectedObject && selectedObject.id === id) {
      setSelectedObject(null);
    }
  };

  const handleObjectCreate = async (newObject: any) => {
    const docRef = await addDoc(collection(db, selectedType), newObject); // Add new document to Firestore
    setData([...data, { id: docRef.id, ...newObject }]); // Add the new object to the local state
    setSelectedObject({ id: docRef.id, ...newObject });
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterChange = (newFilters: Record<string, string>) => {
    setFilters(newFilters);
  };

  const filteredData = data.filter((item) => {
    const matchesSearch =
      !searchTerm ||
      Object.values(item).some(
        (val) =>
          typeof val === "string" &&
          val.toLowerCase().includes(searchTerm.toLowerCase())
      );

    if (!matchesSearch) return false;

    return Object.entries(filters).every(([key, value]) => {
      if (!value) return true;
      return (
        item[key] &&
        item[key].toString().toLowerCase().includes(value.toLowerCase())
      );
    });
  });

  return (
    <div className="container mx-auto p-2 h-full overflow-y-auto">
      <motion.h1 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="text-3xl font-bold mb-6 text-center text-bytesplash-blue"
      >
        Manipulate Objects
      </motion.h1>

      <div className="grid grid-cols-1 gap-6 flex-grow">
        <div className="flex flex-wrap gap-2 justify-center">
          {objectTypes.map((type) => (
            <motion.div
              key={type}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Button
                variant={selectedType === type ? "default" : "outline"}
                className={
                  selectedType === type
                    ? "bg-bytesplash-blue hover:bg-bytesplash-blue/90"
                    : ""
                }
                onClick={() => handleTypeSelect(type)}
              >
                {type}
              </Button>
            </motion.div>
          ))}
        </div>

        <ResizablePanelGroup direction="horizontal" className="flex-grow rounded-lg border">
          <ResizablePanel defaultSize={60} minSize={30}>
            <Tabs 
              defaultValue="all" 
              value={activeTab} 
              onValueChange={setActiveTab} 
              className="w-full h-full flex flex-col"
            >
              <div className="p-4 flex-grow flex flex-col">
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger value="all">All Objects</TabsTrigger>
                  <TabsTrigger value="filters">Filters & Advanced</TabsTrigger>
                </TabsList>

                <TabsContent value="all" className="flex-grow">
                  <Card className="h-full flex flex-col">
                    <CardHeader className="pb-3">
                      <div className="flex justify-between items-center">
                        <CardTitle>All {selectedType}</CardTitle>
                        <div className="flex items-center gap-2 w-1/2">
                          <div className="relative flex-1">
                            <Search className="absolute left-2 top-3 h-4 w-4 text-muted-foreground" />
                            <Input
                              placeholder="Search by ID, name, email..."
                              className="pl-8"
                              value={searchTerm}
                              onChange={handleSearch}
                            />
                          </div>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => setShowFilters(!showFilters)}
                          >
                            <Filter className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="flex-grow overflow-hidden">
                      {showFilters && (
                        <div className="mb-4">
                          <ObjectFilters
                            objectType={selectedType}
                            onFilterChange={handleFilterChange}
                          />
                        </div>
                      )}
                      <ObjectsList
                        selectedType={selectedType}
                        data={filteredData}
                        onSelect={handleObjectSelect}
                        onDelete={handleObjectDelete}
                        selectedObjectId={selectedObject?.id}
                      />
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="filters" className="flex-grow">
                  <Card className="h-full">
                    <CardHeader>
                      <CardTitle>Advanced Filters</CardTitle>
                    </CardHeader>
                    <CardContent className="h-full">
                      <ObjectFilters
                        objectType={selectedType}
                        onFilterChange={handleFilterChange}
                        expanded={true}
                      />
                    </CardContent>
                  </Card>
                </TabsContent>
              </div>
            </Tabs>
          </ResizablePanel>
          
          <ResizableHandle withHandle />
          
          <ResizablePanel defaultSize={40} minSize={30}>
            <Card className="h-full flex flex-col">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Selected Object</span>
                  {/* {selectedObject && (
                    <div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleObjectDelete(selectedObject.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  )} */}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-grow overflow-hidden">
                <ObjectEditor
                  object={selectedObject}
                  onChange={setSelectedObject}
                  onSave={handleObjectUpdate}
                  onCreate={handleObjectCreate}
                  objectType={selectedType}
                />
              </CardContent>
            </Card>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    </div>
  );
}